import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, ChevronRight, ShoppingBag, Clock, MapPin, Plus, Trash2, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

const STATUS_COLORS = {
  delivered: 'bg-green-100 text-green-700',
  confirmed: 'bg-blue-100 text-blue-700',
  preparing: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-gray-100 text-gray-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  ready: 'bg-teal-100 text-teal-700',
  expired: 'bg-gray-200 text-gray-500',
  rejected: 'bg-red-100 text-red-700'
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, setUser } = useApp();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState('');
  const emptyAddress = { label: 'Home', line1: '', line2: '', city: '', postcode: '' };
  const [addressForm, setAddressForm] = useState(emptyAddress);

  useEffect(() => {
    if (user && activeTab === 'orders') {
      setLoading(true);
      api.getOrders()
        .then(data => setOrders(data))
        .catch(() => setOrders([]))
        .finally(() => setLoading(false));
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user && activeTab === 'addresses') {
      setAddressesLoading(true);
      api.getCustomerAddresses()
        .then(data => setAddresses(data || []))
        .catch(() => setAddressError('We could not load your saved addresses.'))
        .finally(() => setAddressesLoading(false));
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user && editMode) {
      setEditForm({ name: user.name || '', phone: user.phone || '' });
    }
  }, [user, editMode]);

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const updated = await api.updateProfile(editForm);
      setUser({ ...user, name: updated.name, phone: updated.phone });
      setSaveSuccess(true);
      setTimeout(() => {
        setEditMode(false);
        setSaveSuccess(false);
      }, 1500);
    } catch (err) {
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
          <p className="text-gray-500 mb-6 text-sm">View your orders and manage your profile</p>
          <button onClick={() => navigate('/login')} className="bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const handleSaveAddress = async () => {
    if (!addressForm.line1.trim() || !addressForm.city.trim() || !addressForm.postcode.trim()) {
      setAddressError('Address line 1, town or city, and postcode are required.');
      return;
    }
    setAddressSaving(true);
    setAddressError('');
    try {
      const saved = await api.saveCustomerAddress({
        ...addressForm,
        postcode: addressForm.postcode.trim().toUpperCase()
      });
      setAddresses(current => {
        const withoutSaved = current.filter(address => address.id !== saved.id);
        return [saved, ...withoutSaved];
      });
      setAddressForm(emptyAddress);
      setAddressFormOpen(false);
    } catch (err) {
      setAddressError(err.response?.data?.detail || 'We could not save that address.');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    setAddressError('');
    try {
      await api.deleteCustomerAddress(addressId);
      setAddresses(current => {
        const remaining = current.filter(address => address.id !== addressId);
        if (remaining.length && !remaining.some(address => address.isDefault)) {
          return remaining.map((address, index) => ({ ...address, isDefault: index === 0 }));
        }
        return remaining;
      });
    } catch (err) {
      setAddressError(err.response?.data?.detail || 'We could not remove that address.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">My Account</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-amber-700 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{user.name}</h2>
              <p className="text-gray-500 text-sm">{user.email}</p>
              {user.isAdmin && (
                <span className="inline-block text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-md mt-1">Admin</span>
              )}
            </div>
            {user.isAdmin && (
              <button onClick={() => navigate('/admin')} className="ml-auto bg-amber-700 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-800 transition-colors">
                Admin Panel
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-xl">
          {[{ id: 'orders', label: 'Orders', icon: Package }, { id: 'addresses', label: 'Addresses', icon: MapPin }, { id: 'profile', label: 'Profile', icon: User }].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1.5 ${activeTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Orders */}
        {activeTab === 'orders' && (
          <div className="space-y-3">
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ borderWidth: 2 }} />
                <p className="text-gray-400 text-sm">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">No orders yet</p>
                <p className="text-gray-400 text-sm mt-1">Your past orders will appear here</p>
                <button onClick={() => navigate('/')} className="mt-4 bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
                  Start Ordering
                </button>
              </div>
            ) : (
              orders.map(order => {
                const isExpiredOrRejected = order.status === 'expired' || order.status === 'rejected';
                return (
                  <div 
                    key={order.id} 
                    className={`bg-white rounded-2xl border border-gray-200 p-4 transition-colors ${
                      isExpiredOrRejected ? 'opacity-75 cursor-default' : 'hover:border-amber-200 cursor-pointer'
                    }`}
                    onClick={() => !isExpiredOrRejected && navigate(`/order-status/${order.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Order #{order.id.slice(-8).toUpperCase()}</p>
                        <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>
                          {order.status?.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">{order.type}</span>
                      </div>
                    </div>
                    {isExpiredOrRejected && (
                      <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                        <p className="text-xs text-gray-600">
                          {order.status === 'expired' 
                            ? 'This order was not confirmed in time and has expired.'
                            : 'This order was rejected by the restaurant.'}
                        </p>
                      </div>
                    )}
                    <div className="space-y-1 mb-3">
                      {order.items?.slice(0, 3).map((item, i) => (
                        <p key={i} className="text-sm text-gray-600">
                          {item.quantity}x {item.name}<span className="text-gray-400 ml-1">· £{(item.price * item.quantity).toFixed(2)}</span>
                        </p>
                      ))}
                      {order.items?.length > 3 && <p className="text-xs text-gray-400">+{order.items.length - 3} more items</p>}
                    </div>
                    <div className="border-t border-gray-100 pt-3 space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal:</span>
                        <span>£{(order.subtotal || order.total || 0).toFixed(2)}</span>
                      </div>
                      {order.deliveryFee > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Delivery Fee:</span>
                          <span>£{order.deliveryFee.toFixed(2)}</span>
                        </div>
                      )}
                      {order.serviceCharge > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Service Charge:</span>
                          <span>£{order.serviceCharge.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-100">
                        <span>Total:</span>
                        <span>£{((order.subtotal || order.total || 0) + (order.deliveryFee || 0) + (order.serviceCharge || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end pt-2">
                      {!isExpiredOrRejected && (
                        <button onClick={(e) => { e.stopPropagation(); navigate('/'); }}
                          className="text-sm text-amber-700 font-semibold hover:text-amber-800 flex items-center gap-1">
                          Reorder<ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Saved delivery addresses */}
        {activeTab === 'addresses' && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Delivery addresses</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Used for faster checkout. Delivery availability is checked each time.</p>
                </div>
                {!addressFormOpen && (
                  <button onClick={() => { setAddressFormOpen(true); setAddressError(''); }} className="inline-flex items-center gap-1 text-sm text-amber-700 font-semibold hover:text-amber-800 whitespace-nowrap">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                )}
              </div>

              {addressFormOpen && (
                <div className="p-4 border-b border-gray-100 space-y-3 bg-gray-50/70">
                  <div className="flex items-center justify-between"><h4 className="text-sm font-semibold text-gray-900">Add a new address</h4><button onClick={() => { setAddressFormOpen(false); setAddressForm(emptyAddress); setAddressError(''); }} aria-label="Close address form" className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button></div>
                  <input value={addressForm.label} onChange={e => setAddressForm(current => ({ ...current, label: e.target.value }))} placeholder="Label, e.g. Home" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
                  <input value={addressForm.line1} onChange={e => setAddressForm(current => ({ ...current, line1: e.target.value }))} placeholder="Address line 1 *" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
                  <input value={addressForm.line2} onChange={e => setAddressForm(current => ({ ...current, line2: e.target.value }))} placeholder="Address line 2 (optional)" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
                  <div className="grid grid-cols-2 gap-3">
                    <input value={addressForm.city} onChange={e => setAddressForm(current => ({ ...current, city: e.target.value }))} placeholder="Town or city *" className="min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
                    <input value={addressForm.postcode} onChange={e => setAddressForm(current => ({ ...current, postcode: e.target.value.toUpperCase() }))} placeholder="Postcode *" className="min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-amber-500" />
                  </div>
                  <button onClick={handleSaveAddress} disabled={addressSaving} className="w-full bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-60">
                    {addressSaving ? 'Saving…' : 'Save address'}
                  </button>
                </div>
              )}

              {addressError && <p className="mx-4 mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{addressError}</p>}

              {addressesLoading ? (
                <div className="p-8 text-center"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : addresses.length ? (
                <div className="divide-y divide-gray-100">
                  {addresses.map(address => (
                    <div key={address.id} className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-gray-500" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2"><p className="font-semibold text-sm text-gray-900">{address.label || 'Saved address'}</p>{address.isDefault && <span className="text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 rounded-full px-2 py-0.5">Default</span>}</div>
                        <p className="text-sm text-gray-600 mt-1">{address.line1}{address.line2 ? `, ${address.line2}` : ''}</p>
                        <p className="text-sm text-gray-500">{address.city}, {address.postcode}</p>
                      </div>
                      <button onClick={() => handleDeleteAddress(address.id)} aria-label={`Remove ${address.label || 'address'}`} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ) : !addressFormOpen ? (
                <div className="p-8 text-center"><MapPin className="w-9 h-9 text-gray-300 mx-auto mb-2" /><p className="text-sm font-medium text-gray-600">No saved addresses</p><p className="text-xs text-gray-400 mt-1">Add one here or during your next delivery checkout.</p></div>
              ) : null}
            </div>
          </div>
        )}

        {/* Profile */}
        {activeTab === 'profile' && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Personal Information</h3>
                {!editMode && (
                  <button 
                    onClick={() => setEditMode(true)}
                    data-testid="edit-profile-button"
                    className="text-sm text-amber-700 font-semibold hover:text-amber-800"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Full Name</label>
                  {editMode ? (
                    <input 
                      type="text" 
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      data-testid="profile-name-input"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" 
                    />
                  ) : (
                    <p className="text-sm text-gray-900 py-3">{user.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email Address</label>
                  <p className="text-sm text-gray-900 py-3">{user.email}</p>
                  {editMode && <p className="text-xs text-gray-400">Email cannot be changed</p>}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Phone Number</label>
                  {editMode ? (
                    <input 
                      type="tel" 
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="+44 XXXX XXXXXX"
                      data-testid="profile-phone-input"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" 
                    />
                  ) : (
                    <p className="text-sm text-gray-900 py-3">{user.phone || 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>
            {editMode && (
              <div className="flex gap-3">
                <button 
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                  className="flex-1 border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveProfile}
                  disabled={saving}
                  data-testid="save-profile-button"
                  className="flex-1 bg-gray-900 text-white py-3.5 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    <>Saved!</>
                  ) : (
                    <>Save Changes</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
